import React, { useState } from 'react';
import { TrendingDownIcon, CreditCardIcon, HomeIcon, CarIcon, GraduationCapIcon, AlertCircleIcon, CheckCircleIcon, CalendarIcon, TrendingUpIcon, ZapIcon, TargetIcon } from 'lucide-react';
interface Debt {
  id: string;
  name: string;
  type: 'credit-card' | 'mortgage' | 'car-loan' | 'student-loan' | 'personal-loan';
  balance: number;
  interestRate: number;
  minimumPayment: number;
  monthlyPayment: number;
  icon: React.ElementType;
  color: string;
}
type PayoffStrategy = 'avalanche' | 'snowball';
export function DebtManagement() {
  const [monthlyBudget, setMonthlyBudget] = useState(1500);
  const [strategy, setStrategy] = useState<PayoffStrategy>('avalanche');
  const [debts, setDebts] = useState<Debt[]>([{
    id: '1',
    name: 'Credit Card (Visa)',
    type: 'credit-card',
    balance: 1281,
    interestRate: 19.99,
    minimumPayment: 90,
    monthlyPayment: 90,
    icon: CreditCardIcon,
    color: 'text-red-500'
  }, {
    id: '2',
    name: 'Credit Card (Mastercard)',
    type: 'credit-card',
    balance: 2300,
    interestRate: 22.5,
    minimumPayment: 46,
    monthlyPayment: 46,
    icon: CreditCardIcon,
    color: 'text-orange-500'
  }, {
    id: '3',
    name: 'Car Loan',
    type: 'car-loan',
    balance: 8500,
    interestRate: 6.5,
    minimumPayment: 320,
    monthlyPayment: 320,
    icon: CarIcon,
    color: 'text-blue-500'
  }]);
  const totalDebt = debts.reduce((sum, debt) => sum + debt.balance, 0);
  const totalMinimumPayment = debts.reduce((sum, debt) => sum + debt.minimumPayment, 0);
  const extraPayment = Math.max(0, monthlyBudget - totalMinimumPayment);
  const calculateMonthlyInterest = (debt: Debt) => {
    return debt.balance * (debt.interestRate / 100) / 12;
  };
  const calculatePrincipalPayment = (debt: Debt) => {
    return debt.monthlyPayment - calculateMonthlyInterest(debt);
  };
  const getSortedDebts = () => {
    if (strategy === 'avalanche') {
      return [...debts].sort((a, b) => b.interestRate - a.interestRate);
    } else {
      return [...debts].sort((a, b) => a.balance - b.balance);
    }
  };
  const calculatePayoffPlan = () => {
    const sortedDebts = getSortedDebts();
    let remainingBudget = extraPayment;
    return sortedDebts.map((debt, index) => {
      const isTarget = index === 0 && remainingBudget > 0;
      const additionalPayment = isTarget ? remainingBudget : 0;
      const totalPayment = debt.minimumPayment + additionalPayment;
      const monthlyInterest = calculateMonthlyInterest(debt);
      const principalPayment = totalPayment - monthlyInterest;
      const monthsToPayoff = principalPayment > 0 ? Math.ceil(debt.balance / principalPayment) : 999;
      return {
        ...debt,
        additionalPayment,
        totalPayment,
        monthlyInterest,
        principalPayment,
        monthsToPayoff,
        isTarget
      };
    });
  };
  const payoffPlan = calculatePayoffPlan();
  const totalMonthlyInterest = debts.reduce((sum, debt) => sum + calculateMonthlyInterest(debt), 0);
  const totalMonthlyPrincipal = monthlyBudget - totalMonthlyInterest;
  return <div className="min-h-screen bg-gray-50 dark:bg-gray-900 p-4 md:p-6" data-id="element-1118">
      <div className="max-w-7xl mx-auto space-y-6" data-id="element-1119">
        {/* Header */}
        <div className="flex items-center justify-between" data-id="element-1120">
          <div data-id="element-1121">
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white flex items-center" data-id="element-1122">
              <TrendingDownIcon className="w-8 h-8 mr-3 text-primary" data-id="element-1123" />
              Debt Management
            </h1>
            <p className="text-gray-600 dark:text-gray-400 mt-1" data-id="element-1124">
              Take control of your debts with smart payoff strategies
            </p>
          </div>
        </div>
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-1125">
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm" data-id="element-1126">
            <div className="flex items-center justify-between mb-2" data-id="element-1127">
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1128">
                Total Debt
              </p>
              <TrendingDownIcon className="w-5 h-5 text-red-500" data-id="element-1129" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-1130">
              £{totalDebt.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1131">
              Across {debts.length} accounts
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm" data-id="element-1132">
            <div className="flex items-center justify-between mb-2" data-id="element-1133">
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1134">
                Monthly Budget
              </p>
              <TargetIcon className="w-5 h-5 text-primary" data-id="element-1135" />
            </div>
            <p className="text-3xl font-bold text-gray-900 dark:text-white" data-id="element-1136">
              £{monthlyBudget.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500 mt-1" data-id="element-1137">
              £{extraPayment.toFixed(2)} extra after minimums
            </p>
          </div>
          <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm" data-id="element-1138">
            <div className="flex items-center justify-between mb-2" data-id="element-1139">
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1140">
                Interest vs Principal
              </p>
              <TrendingUpIcon className="w-5 h-5 text-orange-500" data-id="element-1141" />
            </div>
            <div className="space-y-2" data-id="element-1142">
              <div className="flex justify-between text-sm" data-id="element-1143">
                <span className="text-gray-600 dark:text-gray-400" data-id="element-1144">
                  Interest:
                </span>
                <span className="font-semibold text-orange-500" data-id="element-1145">
                  £{totalMonthlyInterest.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between text-sm" data-id="element-1146">
                <span className="text-gray-600 dark:text-gray-400" data-id="element-1147">
                  Principal:
                </span>
                <span className="font-semibold text-green-500" data-id="element-1148">
                  £{totalMonthlyPrincipal.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
        </div>
        {/* Monthly Budget Input */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm" data-id="element-1149">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1150">
            Set Your Monthly Debt Payment Budget
          </h2>
          <div className="space-y-4" data-id="element-1151">
            <div data-id="element-1152">
              <div className="flex justify-between items-center mb-2" data-id="element-1153">
                <label className="text-sm font-medium text-gray-700 dark:text-gray-300" data-id="element-1154">
                  Monthly Budget: £{monthlyBudget}
                </label>
                <span className="text-xs text-gray-500" data-id="element-1155">
                  Minimum required: £{totalMinimumPayment.toFixed(2)}
                </span>
              </div>
              <input type="range" min={totalMinimumPayment} max={5000} step={50} value={monthlyBudget} onChange={e => setMonthlyBudget(parseInt(e.target.value))} className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-primary" data-id="element-1156" />
              <div className="flex justify-between text-xs text-gray-500 mt-1" data-id="element-1157">
                <span data-id="element-1158">£{totalMinimumPayment.toFixed(0)}</span>
                <span data-id="element-1159">£5,000</span>
              </div>
            </div>
            {extraPayment > 0 && <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4" data-id="element-1160">
                <div className="flex items-start" data-id="element-1161">
                  <CheckCircleIcon className="w-5 h-5 text-green-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1162" />
                  <div data-id="element-1163">
                    <p className="text-sm font-medium text-green-900 dark:text-green-100" data-id="element-1164">
                      Great! You have £{extraPayment.toFixed(2)} extra
                    </p>
                    <p className="text-xs text-green-700 dark:text-green-300 mt-1" data-id="element-1165">
                      This extra amount will be applied to accelerate your debt
                      payoff
                    </p>
                  </div>
                </div>
              </div>}
            {extraPayment <= 0 && <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4" data-id="element-1166">
                <div className="flex items-start" data-id="element-1167">
                  <AlertCircleIcon className="w-5 h-5 text-amber-600 mr-2 flex-shrink-0 mt-0.5" data-id="element-1168" />
                  <div data-id="element-1169">
                    <p className="text-sm font-medium text-amber-900 dark:text-amber-100" data-id="element-1170">
                      Budget equals minimum payments
                    </p>
                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1" data-id="element-1171">
                      Consider increasing your budget to pay off debt faster
                    </p>
                  </div>
                </div>
              </div>}
          </div>
        </div>
        {/* Strategy Selection */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm" data-id="element-1172">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1173">
            Choose Your Payoff Strategy
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-1174">
            <button onClick={() => setStrategy('avalanche')} className={`p-6 rounded-xl border-2 transition-all text-left ${strategy === 'avalanche' ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'}`} data-id="element-1175">
              <div className="flex items-center mb-3" data-id="element-1176">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${strategy === 'avalanche' ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`} data-id="element-1177">
                  <TrendingUpIcon className="w-6 h-6" data-id="element-1178" />
                </div>
                <div className="ml-3" data-id="element-1179">
                  <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1180">
                    Avalanche Method
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-1181">Save more on interest</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1182">
                Pay off highest interest rate debts first. This method saves you
                the most money on interest payments over time.
              </p>
            </button>
            <button onClick={() => setStrategy('snowball')} className={`p-6 rounded-xl border-2 transition-all text-left ${strategy === 'snowball' ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-gray-700 hover:border-primary/50'}`} data-id="element-1183">
              <div className="flex items-center mb-3" data-id="element-1184">
                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${strategy === 'snowball' ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-400'}`} data-id="element-1185">
                  <ZapIcon className="w-6 h-6" data-id="element-1186" />
                </div>
                <div className="ml-3" data-id="element-1187">
                  <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1188">
                    Snowball Method
                  </h3>
                  <p className="text-xs text-gray-500" data-id="element-1189">Build momentum</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400" data-id="element-1190">
                Pay off smallest balances first. This method provides quick wins
                and builds positive momentum to stay motivated.
              </p>
            </button>
          </div>
        </div>
        {/* Payoff Plan */}
        <div className="bg-white dark:bg-gray-800 rounded-xl p-6 shadow-sm" data-id="element-1191">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4" data-id="element-1192">
            Your Payoff Plan
          </h2>
          <div className="space-y-4" data-id="element-1193">
            {payoffPlan.map((debt, index) => <div key={debt.id} className={`border rounded-xl p-5 ${debt.isTarget ? 'border-primary bg-primary/5' : 'border-gray-200 dark:border-gray-700'}`} data-id="element-1194">
                <div className="flex items-start justify-between mb-4" data-id="element-1195">
                  <div className="flex items-center flex-1" data-id="element-1196">
                    <div className={`w-12 h-12 rounded-full flex items-center justify-center ${debt.isTarget ? 'bg-primary text-white' : 'bg-gray-100 dark:bg-gray-700'}`} data-id="element-1197">
                      <debt.icon className="w-6 h-6" data-id="element-1198" />
                    </div>
                    <div className="ml-4 flex-1" data-id="element-1199">
                      <div className="flex items-center" data-id="element-1200">
                        <h3 className="font-semibold text-gray-900 dark:text-white" data-id="element-1201">
                          {debt.name}
                        </h3>
                        {debt.isTarget && <span className="ml-2 px-2 py-1 bg-primary text-white text-xs rounded-full" data-id="element-1202">
                            Priority Target
                          </span>}
                        {index === 0 && !debt.isTarget && <span className="ml-2 px-2 py-1 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300 text-xs rounded-full" data-id="element-1203">
                            Next in Line
                          </span>}
                      </div>
                      <p className="text-sm text-gray-600 dark:text-gray-400 mt-1" data-id="element-1204">
                        Balance: £{debt.balance.toLocaleString()} • Interest
                        Rate: {debt.interestRate}%
                      </p>
                    </div>
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4" data-id="element-1205">
                  <div data-id="element-1206">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1207">
                      Monthly Payment
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-1208">
                      £{debt.totalPayment.toFixed(2)}
                    </p>
                    {debt.additionalPayment > 0 && <p className="text-xs text-green-600" data-id="element-1209">
                        +£{debt.additionalPayment.toFixed(2)} extra
                      </p>}
                  </div>
                  <div data-id="element-1210">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1211">
                      Interest Payment
                    </p>
                    <p className="font-semibold text-orange-500" data-id="element-1212">
                      £{debt.monthlyInterest.toFixed(2)}
                    </p>
                  </div>
                  <div data-id="element-1213">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1214">
                      Principal Payment
                    </p>
                    <p className="font-semibold text-green-500" data-id="element-1215">
                      £{debt.principalPayment.toFixed(2)}
                    </p>
                  </div>
                  <div data-id="element-1216">
                    <p className="text-xs text-gray-500 mb-1" data-id="element-1217">
                      Payoff Timeline
                    </p>
                    <p className="font-semibold text-gray-900 dark:text-white" data-id="element-1218">
                      {debt.monthsToPayoff < 999 ? `${debt.monthsToPayoff} months` : 'N/A'}
                    </p>
                  </div>
                </div>
                <div className="space-y-2" data-id="element-1219">
                  <div className="flex justify-between text-xs text-gray-600 dark:text-gray-400" data-id="element-1220">
                    <span data-id="element-1221">Progress</span>
                    <span data-id="element-1222">
                      {debt.principalPayment > 0 ? `${(debt.principalPayment / debt.balance * 100).toFixed(1)}% per month` : '0%'}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2" data-id="element-1223">
                    <div className="bg-gradient-to-r from-primary to-secondary h-2 rounded-full" style={{
                  width: `${Math.min(100, debt.principalPayment / debt.balance * 100 * 12)}%`
                }} data-id="element-1224"></div>
                  </div>
                </div>
              </div>)}
          </div>
        </div>
      </div>
    </div>;
}